import type { FormContextType, ObjectFieldTemplateProps, RJSFSchema, StrictRJSFSchema } from '@rjsf/utils';
import clsx from 'clsx';
import { ChevronRight } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import styles from './style.module.sass';

const getGroupedProperties = <T, S extends StrictRJSFSchema = RJSFSchema, F extends FormContextType = any>(
	fields: Array<string>,
	props: ObjectFieldTemplateProps<T, S, F>,
	renderedFieldNames: Set<string>
) =>
	props.properties.filter(({ name }) => {
		if (!fields.includes(name) || renderedFieldNames.has(name)) {
			return false;
		}

		renderedFieldNames.add(name);
		return true;
	});

const ObjectFieldTemplate = <T, S extends StrictRJSFSchema = RJSFSchema, F extends FormContextType = any>(
	props: ObjectFieldTemplateProps<T, S, F>
) => {
	const { t } = useTranslation();

	const { properties, formContext, uiSchema } = props;
	const groups = (formContext?.groups as Array<{ name: string; fields: Array<string> }>) ?? [];

	const isTopLevelObject = props.idSchema.$id === 'root';
	const renderedFieldNames = new Set<string>();

	return (
		<div
			data-role="ObjectFieldTemplate"
			// TODO: Extend UISchema props type
			// eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
			className={clsx(uiSchema?.props?.className, { 'flex flex-col gap-y-5': !uiSchema?.props?.className })}
		>
			{isTopLevelObject && groups.length
				? groups.map(({ name, fields }, idx) => {
						const groupedProperties = getGroupedProperties<T, S, F>(fields, props, renderedFieldNames);

						if (!groupedProperties.length) {
							return null;
						}

						if (!name) {
							return groupedProperties.map((property) => property.content);
						}

						return (
							<div
								id={name}
								key={name}
								className={clsx('relative flex flex-col gap-y-6 py-6', {
									'border-t': !!idx,
									'pt-0': !idx,
								})}
							>
								{name !== 'default' && (
									<div className="relative text-2xl">
										<ChevronRight className={clsx('absolute', styles.chevron)} />

										{t(`templatesFieldGroups.${name}`)}
									</div>
								)}

								{groupedProperties.map((property) => property.content)}
							</div>
						);
					})
				: properties.map((property) => property.content)}
		</div>
	);
};

export { ObjectFieldTemplate };
